import React, { useMemo, useEffect, useState, useCallback } from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import Icon from '../../elementals/icon'
import Button from '../../elementals/button'
import AnimatedProgressBar from '../../elementals/animatedProgressBar'
import useElementDimensions from '../../../hooks/useElementDimensions'
import useBreakpoints from '../../../hooks/useBreakpoints'
import { ColorNames } from '../../../constants/colorVars'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const ESTIMATED_CHAR_WIDTH = 8 // Estimated width of a character in pixels
const ICON_AND_PADDING_WIDTH = 66 // Width of the icon and padding around it

const DEFAULT_ICON_PROPS = {
  style: { verticalAlign: 'text-top' },
  fitted: true,
  size: 'large',
  iconStyle: 'regular',
  color: ColorNames.Gray,
}

export interface StepProps {
  onStepClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
    data: {
      completed: boolean
      stepItem: string
      activeStep: number
      [key: string]: unknown
    }
  ) => void
  completed?: boolean
  stepItem: string
  index: number
  activeStep: number
  prevStepCompleted?: boolean
  elmId?: string
  viewMode?: boolean
  isLastStep?: boolean
  horizontalLabels?: boolean
  maxWidth?: number
  [key: string]: unknown
}

const Step: React.FC<StepProps> = ({
  onStepClick,
  completed = false,
  stepItem,
  index,
  activeStep,
  prevStepCompleted,
  elmId = 'default',
  viewMode,
  isLastStep = false,
  horizontalLabels = false,
  maxWidth,
  ...rest
}) => {
  const isClickable = useMemo(
    () =>
      !!onStepClick &&
      index !== activeStep &&
      (completed || prevStepCompleted || viewMode),
    [onStepClick, completed, index, viewMode, prevStepCompleted, activeStep]
  )
  const style =
    // eslint-disable-next-line no-nested-ternary
    isLastStep && horizontalLabels
      ? {
          maxWidth: `${
            stepItem.length * ESTIMATED_CHAR_WIDTH + ICON_AND_PADDING_WIDTH
          }px`,
        }
      : horizontalLabels && maxWidth
      ? {
          maxWidth: `${maxWidth}px`,
        }
      : {}
  return (
    <div
      style={style}
      className={cx('step-container', { 'horizontal-mode': horizontalLabels })}
      data-elm-id={`step_container_${elmId}`}
    >
      {/* Connector container */}
      {(!isLastStep || horizontalLabels) && (
        <div
          className={cx('connector-container')}
          data-elm-id={`connector_container_${elmId}`}
        >
          {/* Connector line */}
          {!isLastStep && (
            <span
              className={
                index < activeStep
                  ? cx('connector-line', 'active')
                  : cx('connector-line')
              }
            />
          )}
          {/* Horizontal label */}
          {horizontalLabels && (
            <span
              className={cx('title-container', 'horizontal-mode')}
              data-elm-id={`title_container_${elmId}`}
            >
              <span
                data-elm-id={`title_${elmId}`}
                className={cx('title', {
                  active: activeStep === index,
                  completed,
                })}
              >
                {stepItem}
              </span>
            </span>
          )}
        </div>
      )}

      <button
        data-elm-id={`step_button_${elmId}`}
        onClick={
          isClickable
            ? e =>
                onStepClick?.(e, {
                  completed,
                  stepItem,
                  activeStep: index,
                  ...rest,
                })
            : // @ts-ignore next
              _.noop // eslint-disable-line
        }
        className={cx('step-button', { clickable: isClickable })}
        type='button'
      >
        <span
          className={cx('content-container')}
          data-elm-id={`content_container_${elmId}`}
        >
          <span
            className={cx('icon-container')}
            data-elm-id={`icon_container_${elmId}`}
          >
            {completed ? (
              <Icon
                circular
                size='large'
                name='check'
                color='white'
                iconStyle='solid'
                className={cx('checked', { active: activeStep === index })}
              />
            ) : (
              <Icon
                name=''
                size='large'
                circular
                className={cx({ active: activeStep === index })}
                iconStyle='solid'
              >
                <span className={cx('icon-text')}>{index + 1}</span>
              </Icon>
            )}
          </span>
          {!horizontalLabels && (
            <span
              className={cx('title-container')}
              data-elm-id={`title_container_${elmId}`}
            >
              <span
                data-elm-id={`title_${elmId}`}
                className={cx('title', {
                  active: activeStep === index,
                  completed,
                })}
              >
                {stepItem}
              </span>
            </span>
          )}
        </span>
      </button>
    </div>
  )
}

export interface StepperStep {
  elmId?: string
  stepItem: string
  completed?: boolean
  [key: string]: unknown
}

export interface StepperProps {
  activeStep?: number
  steps: StepperStep[]
  className?: string
  onStepClick?: StepProps['onStepClick']
  elmId: string
  viewMode?: boolean
  size?: string
  onClose?: () => void
  /**
   * Force the compact layout (current-step title + progress bar + prev/next
   * chevrons) regardless of viewport. When omitted, layout is responsive:
   * compact on `mobile`, full otherwise.
   */
  compact?: boolean
}

const Stepper: React.FC<StepperProps> = ({
  activeStep = 0,
  steps,
  className,
  onStepClick,
  elmId,
  viewMode = false,
  size = 'medium',
  onClose,
  compact,
}) => {
  const canGoToNextStep = useMemo(
    () => onStepClick && (steps[activeStep]?.completed || viewMode),
    [onStepClick, steps, activeStep, viewMode]
  )
  const [horizontalLabels, setHorizontalLabels] = useState(false)
  const stepperContainerRef = React.useRef<HTMLDivElement>(null)
  const stepperDimensions = useElementDimensions(stepperContainerRef)
  const breakpoint = useBreakpoints()
  // `compact` forces the compact layout on any viewport; otherwise fall back to
  // the responsive default (compact only on mobile).
  const useCompact = compact ?? breakpoint === 'mobile'

  const maxTitleWidth = useMemo(() => {
    let maxWidth = 0
    steps.forEach(step => {
      const titleWidth = step.stepItem.length * ESTIMATED_CHAR_WIDTH
      if (titleWidth > maxWidth) {
        maxWidth = titleWidth
      }
    })
    return maxWidth
  }, [steps])

  useEffect(() => {
    const totalWidth = steps.length * (maxTitleWidth + ICON_AND_PADDING_WIDTH)
    setHorizontalLabels(totalWidth <= (stepperDimensions?.width || 0))
  }, [steps, maxTitleWidth, elmId, stepperDimensions?.width, breakpoint])

  const nextPrevHandler = useCallback(
    // function takes an event and a direction ('next' or 'prev')
    (e: React.MouseEvent<HTMLButtonElement>, direction: 'next' | 'prev') => {
      if (onStepClick) {
        const nextStepIndex =
          direction === 'next' ? activeStep + 1 : activeStep - 1
        if (
          nextStepIndex >= 0 &&
          nextStepIndex < steps.length &&
          (direction === 'prev' || (direction === 'next' && canGoToNextStep))
        ) {
          // @ts-ignore next
          onStepClick(e, { activeStep: nextStepIndex, ...steps[nextStepIndex] })
        }
      }
    },
    [onStepClick, activeStep, steps, canGoToNextStep]
  )

  return (
    <div ref={stepperContainerRef}>
      {!useCompact && (
        <div
          className={cx('default-stepper', className)}
          data-elm-id={`wrapper_container_${elmId}`}
        >
          <div
            className={cx('stepper-container', size)}
            data-elm-id={`stepper_container_${elmId}`}
          >
            {steps.map(
              ({ elmId: stepElmId, stepItem, completed, ...rest }, index) => (
                <Step
                  elmId={stepElmId}
                  key={stepItem}
                  stepItem={stepItem}
                  activeStep={activeStep}
                  onStepClick={onStepClick}
                  completed={!!completed}
                  index={index}
                  prevStepCompleted={index > 0 && !!steps[index - 1].completed}
                  viewMode={viewMode}
                  isLastStep={index === steps.length - 1}
                  horizontalLabels={horizontalLabels}
                  maxWidth={maxTitleWidth * 2 + ICON_AND_PADDING_WIDTH}
                  {...rest}
                />
              )
            )}
          </div>
        </div>
      )}
      {useCompact && (
        <div className={cx('mobile-stepper', className)}>
          <div
            className={cx('main')}
            data-elm-id={`mobile_stepper_container_${elmId}`}
          >
            {activeStep === 0 && onClose ? (
              <Button collapse role='tertiary' onClick={onClose} size='small'>
                {/* @ts-ignore next */}
                <Icon name='xmark' {...DEFAULT_ICON_PROPS} />
              </Button>
            ) : (
              <Button
                collapse
                role='tertiary'
                // eslint-disable-next-line
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                  nextPrevHandler(e, 'prev')
                }
                size='small'
                disabled={activeStep === 0}
              >
                {/* @ts-ignore next */}
                <Icon name='chevron-left' {...DEFAULT_ICON_PROPS} />
              </Button>
            )}
            <div>
              <span className={cx('title')}>{steps[activeStep]?.stepItem}</span>
            </div>
            <Button
              collapse
              role='tertiary'
              // eslint-disable-next-line
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                nextPrevHandler(e, 'next')
              }
              size='small'
              disabled={!canGoToNextStep}
            >
              {/* @ts-ignore next */}
              <Icon name='chevron-right' {...DEFAULT_ICON_PROPS} />
            </Button>
          </div>
          <AnimatedProgressBar
            color={ColorNames.Info}
            percent={((activeStep + 1) / steps.length) * 100}
            rounded
            animationSeconds={0.3}
          />
        </div>
      )}
    </div>
  )
}

export default Stepper
